import { Section } from '@/components/primitives/Section';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/primitives/Badge';
import { DollarSign, Smartphone, MapPin, TrendingUp, Zap, Star } from 'lucide-react';
import { AppStoreBadges } from '@/components/marketing/AppStoreBadges';
import { motion } from 'framer-motion';
import { SEO, SEO_CONFIG } from '@/components/primitives/SEO';

export function OperatorsPage() {
  return (
    <>
      <SEO {...SEO_CONFIG.operators} />
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-brand-900 via-brand-800 to-brand-700 text-white overflow-hidden py-20">
        {/* Animated Background Grid */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0f_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0f_1px,transparent_1px)] bg-[size:4rem_4rem]" />
        </div>

        {/* Gradient Orbs */}
        <motion.div 
          className="absolute top-0 right-0 w-[500px] h-[500px] bg-accent-500/30 rounded-full blur-[100px]"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />

        <div className="relative container-default mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-accent-500/20 backdrop-blur-sm border border-accent-500/30 rounded-full mb-6"
          >
            <Zap className="w-4 h-4 text-accent-500" fill="currentColor" />
            <span className="text-sm font-bold text-accent-500 uppercase tracking-wider">Your Winter Side Hustle</span>
          </motion.div>

          <motion.h1 
            className="text-hero mb-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Your snowblower.<br />
            <span className="gradient-text">Your side hustle.</span>
          </motion.h1>

          <motion.p 
            className="text-body-lg text-snow-100 mb-10 max-w-2xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <strong className="font-semibold text-white">Turn winter into income.</strong> You retain 85% base job earnings and 100% of tips. 
            Set your own hours. Pick your radius. <span className="text-accent-500 font-semibold">Guaranteed payout within 24 hours, no bullshit.</span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <Button 
              variant="primary" 
              size="lg" 
              className="bg-accent-500 hover:bg-accent-600 shadow-glow-green"
              onClick={() => window.location.href = '/apply'}
            >
              Apply to become an operator
            </Button>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <Section
        eyebrow="Simple process"
        title="From sign-up to first payout in under a week"
        subtitle="No door knocking. No flyers. Just jobs in your pocket."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          {[
            { step: 1, title: 'Sign up', desc: 'Create account and verify identity—background check takes 24-48 hours' },
            { step: 2, title: 'Get approved', desc: 'Pass verification and you\'re in. Simple as that' },
            { step: 3, title: 'Go online', desc: 'Toggle availability and start accepting job offers instantly' },
            { step: 4, title: 'Get paid', desc: 'Weekly payouts or instant transfer for a 1.5% fee' },
          ].map((item) => (
            <Card key={item.step} variant="outline" className="hover:border-accent-500 transition-all">
              <div className="w-12 h-12 rounded-full bg-accent-500 text-white flex items-center justify-center text-2xl font-bold mb-4">
                {item.step}
              </div>
              <h3 className="font-bold mb-2">{item.title}</h3>
              <p className="text-sm text-neutral-700">{item.desc}</p>
            </Card>
          ))}
        </div>
      </Section>

      {/* Earnings */}
      <Section
        id="earnings"
        eyebrow="Transparent earnings"
        title="Know exactly what you'll make—no hidden fees"
        subtitle="You retain 85% base job earnings and 100% of tips. We guarantee operator payout within 24 hours, no bullshit. Not waiting 2 weeks for pay."
        background="frost"
      >
        <div className="max-w-4xl mx-auto mt-12">
          <Card variant="solid" className="mb-8">
            <h3 className="font-black text-2xl mb-6 text-center">Example Earnings Breakdown</h3>
            <div className="space-y-4">
              <div className="bg-neutral-50 rounded-xl p-6">
                <p className="font-bold text-lg mb-4">10 driveways × $70 each = $700 gross</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Gross earnings:</span>
                    <span className="font-bold">$700</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Platform fee (15%):</span>
                    <span className="font-bold text-red-600">-$105</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between font-black text-lg">
                    <span>Snow event payout pending:</span>
                    <span className="text-brand-500">$595</span>
                  </div>
                </div>
                <p className="text-xs text-neutral-600 mt-4 italic">Plus 100% of any tips received</p>
              </div>
            </div>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card variant="solid" icon={<DollarSign className="w-8 h-8" />}>
              <h3 className="font-bold text-xl mb-2">Average storm: $500</h3>
              <p className="text-sm text-neutral-700">
                Typical earnings per snow event for active operators.
              </p>
            </Card>
            <Card variant="solid" icon={<TrendingUp className="w-8 h-8" />}>
              <h3 className="font-bold text-xl mb-2">Top earners: $3,500</h3>
              <p className="text-sm text-neutral-700">
                Snowblowers on tractors can do 80-120 drives a night during major storms.
              </p>
            </Card>
          </div>
        </div>
      </Section>

      {/* Requirements */}
      <Section
        id="requirements"
        eyebrow="What you need"
        title="Two tiers: Neighbour or Pro"
        subtitle="Start small with a shovel. Go big with a plow truck. Your call."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
          <Card variant="outline" className="border-2 border-brand-500">
            <Badge variant="default" className="mb-4">Casual</Badge>
            <h3 className="text-2xl font-bold mb-4">Neighbour Tier</h3>
            <ul className="space-y-3 text-neutral-700">
              <li className="flex items-start gap-2">
                <span className="text-accent-500 font-bold">✓</span>
                <span>Shovel or single-stage snowblower</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-accent-500 font-bold">✓</span>
                <span>18+ years old with valid ID</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-accent-500 font-bold">✓</span>
                <span>Smartphone (iOS or Android)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-accent-500 font-bold">✓</span>
                <span>Bank account for payouts</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-accent-500 font-bold">✓</span>
                <span>Pass background check</span>
              </li>
            </ul>
          </Card>

          <Card variant="outline" className="border-2 border-accent-500">
            <Badge variant="accent" className="mb-4">Commercial</Badge>
            <h3 className="text-2xl font-bold mb-4">Pro Tier</h3>
            <ul className="space-y-3 text-neutral-700">
              <li className="flex items-start gap-2">
                <span className="text-accent-500 font-bold">✓</span>
                <span>Two-stage snowblower or plow truck</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-accent-500 font-bold">✓</span>
                <span>Business license (GST registered)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-accent-500 font-bold">✓</span>
                <span>Liability insurance (proof required)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-accent-500 font-bold">✓</span>
                <span>All Neighbour tier requirements</span>
              </li>
            </ul>
            <p className="text-sm text-accent-600 mt-4 font-semibold">
              Pro tier unlocks commercial properties and bigger payouts.
            </p>
          </Card>
        </div>
      </Section>

      {/* App Preview */}
      <Section
        id="app"
        eyebrow="Operator app"
        title="Everything you need in your pocket"
        subtitle="Accept jobs, navigate, upload photos, track earnings—all in one app. Download now."
        background="dark"
      >
        {/* Feature Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12 mb-16">
          {[
            { 
              icon: <MapPin className="w-7 h-7" />, 
              title: 'Job offers with distance & payout', 
              desc: '45 seconds to accept or decline—no pressure',
              gradient: 'from-blue-500 to-cyan-500'
            },
            { 
              icon: <Smartphone className="w-7 h-7" />, 
              title: 'Built-in navigation', 
              desc: 'Tap to open in your preferred maps app',
              gradient: 'from-purple-500 to-pink-500'
            },
            { 
              icon: <DollarSign className="w-7 h-7" />, 
              title: 'Real-time earnings tracker', 
              desc: 'See today, this week, and season totals',
              gradient: 'from-green-500 to-emerald-500'
            },
          ].map((feature, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="relative group"
            >
              <div className="relative h-full bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300 hover:scale-105 hover:border-white/20">
                {/* Gradient Icon Background */}
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <div className="text-white">
                    {feature.icon}
                  </div>
                </div>
                
                <h3 className="font-black text-lg mb-2 text-white">{feature.title}</h3>
                <p className="text-sm text-white/70 leading-relaxed">{feature.desc}</p>
                
                {/* Hover glow effect */}
                <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300 pointer-events-none`} />
              </div>
            </motion.div>
          ))}
        </div>
        
        {/* Download Section with better visual hierarchy */}
        <div className="relative">
          {/* Decorative background */}
          <div className="absolute inset-0 bg-gradient-to-r from-accent-500/20 to-brand-500/20 rounded-3xl blur-3xl" />
          
          <div className="relative bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8 md:p-12 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Badge variant="accent" className="mb-6 bg-accent-500 text-white">
                Download Now
              </Badge>
              
              <h3 className="text-3xl md:text-4xl font-black text-white mb-4">
                Start earning this winter
              </h3>
              
              <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
                Available on iOS and Android. Get the app and apply to become an operator today.
              </p>
              
              {/* App Store Badges - Dark variant for dark background */}
              <AppStoreBadges variant="dark" />
              
              {/* Trust indicators */}
              <div className="flex flex-wrap items-center justify-center gap-6 mt-10 pt-8 border-t border-white/10">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  <span className="text-sm text-white/70 font-medium">500+ Active Operators</span>
                </div>
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  <span className="text-sm text-white/70 font-medium">4.8/5 Operator Rating</span>
                </div>
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-green-400" />
                  <span className="text-sm text-white/70 font-medium">$2K avg/month earnings</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </Section>

      {/* CTA */}
      <section className="bg-gradient-to-br from-brand-900 via-brand-800 to-brand-700 text-white py-20">
        <div className="container-default mx-auto px-4 text-center">
          <h2 className="text-section-title mb-4">Ready to turn winter into income?</h2>
          <p className="text-body-lg text-snow-100 mb-8 max-w-2xl mx-auto">
            Join hundreds of operators already earning with SNOWBLOWR. <strong>No door knocking. No flyers. Just jobs.</strong>
          </p>
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
            <Button 
              variant="primary" 
              size="lg" 
              className="bg-accent-500 hover:bg-accent-600 shadow-glow-green font-black"
              onClick={() => window.location.href = '/apply'}
            >
              Apply now →
            </Button>
            <Button 
              variant="secondary" 
              size="lg"
              className="bg-white hover:bg-neutral-50 text-brand-600 font-bold"
              onClick={() => document.getElementById('earnings')?.scrollIntoView({ behavior: 'smooth' })}
            >
              See earnings
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}

